import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';

import { TenantService } from '../services/tenant.service';
import { MemberSummary } from '../models/member.model';
import { AdminTenantDetailComponent } from './tenant-detail.component';
import { LabelBadge } from '../../../../shared/components/label-badge/label-badge';

/** How many member avatars the users card previews. */
const AVATAR_PREVIEW_COUNT = 5;

/**
 * Overview tab of the tenant detail view — the design's at-a-glance cards:
 * Details, Users (count + avatar preview + manage shortcut), Displays.
 *
 * Only ever rendered inside AdminTenantDetailComponent's outlet, so it reads
 * the tenant from the shell via DI instead of refetching. Displays data has
 * no backend yet (tracked in ~/work/core/back-end-TODO.md item 11).
 */
@Component({
  selector: 'app-admin-tenant-overview',
  standalone: true,
  imports: [TranslocoDirective, DatePipe, LabelBadge],
  templateUrl: './tenant-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminTenantOverviewComponent {
  private readonly shell = inject(AdminTenantDetailComponent);
  private readonly tenantService = inject(TenantService);
  private readonly destroyRef = inject(DestroyRef);

  readonly tenant = this.shell.tenant;

  /** null = unknown (fetch failed) → renders a dash. */
  readonly usersTotal = signal<number | null>(null);
  readonly memberPreview = signal<MemberSummary[]>([]);
  readonly extraMemberCount = computed(() =>
    Math.max(0, (this.usersTotal() ?? 0) - this.memberPreview().length),
  );

  /** SSO cell text, e.g. "SAML" or "SAML, OIDC". */
  readonly ssoProviderNames = computed(() =>
    (this.tenant()?.ssoProviders ?? []).map((p) => p.providerId.toUpperCase()).join(', '),
  );

  /** Initial fetch + refetch whenever the shell reports a membership change. */
  private readonly membersOnChange = effect(() => {
    this.shell.memberChange();
    untracked(() => this.fetchMembers());
  });

  initials(user: MemberSummary): string {
    const fromName = `${user.firstName?.charAt(0) ?? ''}${user.lastName?.charAt(0) ?? ''}`;
    return (fromName || user.email.slice(0, 2)).toUpperCase();
  }

  manageUsers(): void {
    this.shell.showTab('users');
  }

  private fetchMembers(): void {
    // Guard: an empty id would collapse into /organizations//members → GET {id="members"}.
    if (!this.shell.tenantId) return;
    this.tenantService
      .getTenantUsers(this.shell.tenantId, 0, AVATAR_PREVIEW_COUNT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.usersTotal.set(res.totalElements);
          this.memberPreview.set(res.data);
        },
        // Card keeps its explicit placeholder state instead of an error toast.
        error: () => {},
      });
  }
}
